/**
 * ESQUEMA CONGELADO — schemaVersion 1.
 *
 * No importa nada de `layers.ts` ni de `canonical-document.ts` A PROPOSITO: una
 * version antigua no puede quedar a merced de la evolucion de la actual. Si este
 * fichero se edita, deja de poder abrirse un documento v1 real. NO SE TOCA.
 */
import { z } from 'zod';

const MillimetersV1 = z.number().finite();
const PointMmV1 = z.object({ x: MillimetersV1, y: MillimetersV1 });

const AffineMatrixV1 = z.object({
  a: z.number().finite(),
  b: z.number().finite(),
  c: z.number().finite(),
  d: z.number().finite(),
  e: MillimetersV1,
  f: MillimetersV1,
});

const ColorV1 = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rgb'),
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
  }),
  z.object({
    type: z.literal('cmyk'),
    c: z.number(),
    m: z.number(),
    y: z.number(),
    k: z.number(),
  }),
  z.object({
    type: z.literal('spot'),
    book: z.string(),
    code: z.string(),
    preview: z.object({ r: z.number(), g: z.number(), b: z.number() }),
  }),
  z.object({ type: z.literal('technical_white') }),
]);

const LayerBaseV1 = z.object({
  id: z.string().uuid(),
  z: z.number().int(),
  transform: AffineMatrixV1,
  anchor: z.enum(['top-left', 'center', 'custom']),
  anchorPoint: PointMmV1.optional(),
  opacity: z.number().min(0).max(1).default(1),
  blend: z.enum(['normal', 'multiply', 'screen']).default('normal'),
  clip: z.object({ polygonMm: z.array(PointMmV1) }).optional(),
  locked: z.boolean().default(false),
});

export const TextLayerV1 = LayerBaseV1.extend({
  kind: z.literal('text'),
  text: z.string(),
  font: z.object({ family: z.string(), version: z.string(), fileHash: z.string() }),
  sizeMm: MillimetersV1,
  tracking: z.number(),
  /** MULTIPLICADOR del tamano de fuente (en v2 pasa a ser `lineHeightMm`). */
  lineHeight: z.number(),
  direction: z.enum(['ltr', 'rtl']).default('ltr'),
  color: ColorV1,
  frozenLayout: z
    .object({
      engine: z.string(),
      engineVersion: z.string(),
      glyphs: z.array(z.object({ id: z.number(), xMm: MillimetersV1, yMm: MillimetersV1 })),
      variationAxes: z.record(z.number()).optional(),
      substitutions: z.array(z.string()).optional(),
      fallbackFontHash: z.string().optional(),
      approvedOutlineAssetId: z.string().optional(),
    })
    .optional(),
});

export const ImageLayerV1 = LayerBaseV1.extend({
  kind: z.literal('image'),
  assetChecksum: z.string(),
  intrinsic: z.object({
    widthPx: z.number(),
    heightPx: z.number(),
    dpi: z.number().optional(),
  }),
});

export const ShapeLayerV1 = LayerBaseV1.extend({
  kind: z.literal('shape'),
  path: z.string(),
  fill: ColorV1.optional(),
  stroke: ColorV1.optional(),
  strokeWidthMm: MillimetersV1.optional(),
});

export const CanonicalDocumentV1 = z.object({
  schemaVersion: z.literal(1),
  origin: z.object({ zoneId: z.string(), axis: z.enum(['y-down', 'y-up']).default('y-down') }),
  canvasMm: z.object({ width: MillimetersV1, height: MillimetersV1 }),
  layers: z.array(z.discriminatedUnion('kind', [TextLayerV1, ImageLayerV1, ShapeLayerV1])),
});

export type CanonicalDocumentV1 = z.infer<typeof CanonicalDocumentV1>;
